import type { NamedValueCacheInterface } from '../../Platform';
import { toBoolean } from '../../Utility';
import { Deferred } from '../../Utility/Deferred';

type LabOptionValues =
    | 'clearBrowserCacheOnIndexedDbWriteFailure'
    | 'proxyExternalAssets'
    | 'useFsVideoProtocol'
    | 'enableVideoInfoOverlayOnStartUp'
    | 'delayVideoInfoOverlayOnStartUp'
    | 'enableVideoPlaybackMonitor'
    | 'announcementDurationOverride'
    | 'viewModeScaleToFill';

/**
 * @deprecated Use `FeatureEnrollment.isEnrolledIn()` or `platform().getNamedValue()` instead.
 */
export class LabOptions {
    #initialized = false;
    #initializationDeferred = new Deferred<void>();

    #metadata = {
        clearBrowserCacheOnIndexedDbWriteFailure: { type: 'boolean', default: true },
        proxyExternalAssets: { type: 'boolean', default: false },
        useFsVideoProtocol: { type: 'boolean', default: false },
        enableVideoInfoOverlayOnStartUp: { type: 'boolean', default: false },
        delayVideoInfoOverlayOnStartUp: { type: 'number', default: 30 },
        enableVideoPlaybackMonitor: { type: 'boolean', default: false },
        announcementDurationOverride: { type: 'number', default: -1 },
        viewModeScaleToFill: { type: 'boolean', default: false },
    };

    #values: Map<string, boolean | number | string>;
    #platform: NamedValueCacheInterface;
    #keyPrefix = 'LabOption/';

    constructor(p: NamedValueCacheInterface) {
        this.#platform = p;
        this.#values = new Map();
    }

    async initialize(): Promise<void> {
        const namedValues = await Promise.allSettled(
            Object.entries(this.#metadata).map((x) => {
                const [key, value] = x;
                return this.getNamedValue(key, value.default);
            })
        );

        for (const result of namedValues) {
            if (result.status === 'rejected') {
                // @todo log this
                continue;
            }

            const [key, value] = result.value;
            this.#values.set(key, value);
        }

        this.#initialized = true;
        this.#initializationDeferred.resolve();
    }

    /**
     * Returns a promise that is resolved or rejected when labOptions is ready for
     * use or has failed to initialize
     */
    ready(): Promise<void> {
        return this.#initializationDeferred.promise;
    }

    get(key: 'clearBrowserCacheOnIndexedDbWriteFailure'): boolean;
    get(key: 'proxyExternalAssets'): boolean;
    get(key: 'useFsVideoProtocol'): boolean;
    get(key: 'enableVideoInfoOverlayOnStartUp'): boolean;
    get(key: 'enableVideoPlaybackMonitor'): boolean;
    get(key: 'viewModeScaleToFill'): boolean;
    get(key: 'delayVideoInfoOverlayOnStartUp'): number;
    get(key: 'announcementDurationOverride'): number;
    get(key: string): boolean | number | string | undefined {
        this.#checkInitialization();
        let value;

        switch (key) {
            case 'clearBrowserCacheOnIndexedDbWriteFailure':
            case 'proxyExternalAssets':
            case 'useFsVideoProtocol':
            case 'enableVideoInfoOverlayOnStartUp':
            case 'enableVideoPlaybackMonitor':
            case 'viewModeScaleToFill':
                value = this.#values.get(key);
                return value === undefined ? this.#metadata[key].default : toBoolean(value, false);

            case 'delayVideoInfoOverlayOnStartUp':
            case 'announcementDurationOverride':
                value = this.#values.get(key);
                return value === undefined ? this.#metadata[key].default : Number(value);
        }

        return undefined;
    }

    #checkInitialization(): void {
        if (!this.#initialized) {
            throw new Error('The Lab Options have not been initialized');
        }
    }

    async getNamedValue<T = undefined>(
        keyName: string,
        defaultValue: T
    ): Promise<[string, string | T]> {
        const keyValue =
            (await this.#platform.getNamedValue(this.#keyPrefix + keyName)) ?? defaultValue;
        return [keyName, keyValue];
    }

    /**
     * Remove specific Lab Option
     */
    async clear(prop: string): Promise<void> {
        await this.#platform.deleteNamedValue(this.#keyPrefix + prop);
        this.#values.delete(prop);
    }

    /**
     * Remove all Lab Options
     */
    async clearAll(): Promise<PromiseSettledResult<void>[]> {
        return Promise.allSettled(Object.keys(this.#metadata).map((prop) => this.clear(prop)));
    }

    /**
     * Set value of given Lab Option
     * @throws {@link Error} if Option does not exist
     */
    async set(prop: LabOptionValues, value: string | number | boolean): Promise<void> {
        if (this.#metadata[prop] === undefined) {
            throw new Error('Lab option does not exist.');
        }

        const type = this.#metadata[prop].type;
        const defaultValue = this.#metadata[prop].default;

        switch (type) {
            case 'boolean':
                value = toBoolean(value, false);
                break;
            case 'string':
                value = String(value);
                break;
            case 'number':
                value = Number(value);
                break;
        }

        if (value == defaultValue) {
            await this.clear(prop);
        } else {
            await this.#platform.setNamedValue(this.#keyPrefix + prop, value);
            this.#values.set(prop, value);
        }
    }

    /**
     * Change the value of given Lab Option without saving
     * @throws {@link Error} if Option does not exist
     */
    async modify(prop: LabOptionValues, value: string | number | boolean): Promise<void> {
        if (this.#metadata[prop] === undefined) {
            throw new Error('Lab option does not exist.');
        }

        const defaultValue = this.#metadata[prop].default;

        if (value == defaultValue) {
            this.#values.delete(prop);
        } else {
            await this.#platform.setNamedValue(this.#keyPrefix + prop, value);
            this.#values.set(prop, value);
        }
    }

    /**
     * Get details about all Lab Options
     */
    getAll(): Record<
        string,
        {
            value: string | number | boolean | null;
            default: string | number | boolean;
            type: string;
        }
    > {
        return Object.fromEntries(
            Object.entries(this.#metadata).map((x) => {
                const [prop, value] = x;
                return [
                    prop,
                    {
                        value: this.#values.get(prop) ?? null,
                        default: value.default,
                        type: value.type,
                    },
                ];
            })
        );
    }
}
